/**
 * zReportSlip — สลิป "รายงานสรุปยอดขายประจำวัน (Z-Report)" ต่อเครื่องต่อวัน
 *
 * ทำไมต้องเป็นสลิป ไม่ใช่ A4: ตอนสรรพากรตรวจเครื่องที่อนุมัติตาม ภ.พ.06 เจ้าหน้าที่จะขอดู
 * "รายงานปิดยอดรอบวันที่พิมพ์ออกจากเครื่องพิมพ์สลิปความร้อน" และตัวสลิปใบนั้นต้องมีบรรทัด
 * **ยอดขายสะสม (Grand Total)** กำกับ — รายงาน A4 หลังบ้านใช้แทนกันไม่ได้
 * (บัญชียืนยัน 2026-09-10 · ดู silver-spoon-api/docs/vat-compliance.md D13)
 *
 * ตัวเลขทุกตัวมาจาก payload ของรายงานภาษีขาย (แท็บแยกตามเครื่อง POS) ตรง ๆ ไม่คำนวณซ้ำ
 * → สลิปกับ A4 เป็นตัวเลขชุดเดียวกันเสมอ ซึ่งเป็นสิ่งที่เจ้าหน้าที่เอามากระทบยอดกัน
 *
 * ⚠️ ยอดบนสลิปนี้ **ไม่ใช่เงินในลิ้นชัก** — เครื่องเริ่มต้นของสาขารับบิลเดลิเวอรี่และบิล
 *    ที่รับเงินจากหลังร้านมาด้วย (D13) จึงต้องแยกบรรทัด "ขายหน้าร้าน / เดลิเวอรี่+หลังร้าน"
 *    ให้ชัด ไม่งั้นแคชเชียร์นับเงินปิดกะแล้วไม่ตรงทุกวัน
 */

import { printPdf } from '@/utils/billReceipt';
import {
  isSupported as escposIsSupported,
  printCanvas,
  renderDocDefToCanvas,
  dotsForPaperWidth,
} from '@/utils/escposPrinter';

const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * ประกอบ doc definition ของสลิป — ใช้หน้ากระดาษชุดเดียวกับใบเสร็จ (utils/billReceipt.js)
 * เพื่อให้ความกว้าง/ระยะขอบตรงกับที่เครื่องพิมพ์ตัวเดียวกันพิมพ์ใบเสร็จออกมา
 */
export function buildZReportDocDefinition({ row, shopTax = {}, paperWidth = '72mm', fmt, t = (s) => s, brandName }) {
  const is58mm = String(paperWidth) === '58mm';
  const P = is58mm
    ? { pageWidth: 136.06, sideMargin: 2.83, sepWidth: 130, amt: 48 }
    : { pageWidth: 204.09, sideMargin: 4.25, sepWidth: 195, amt: 70 };

  const line = () => ({
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: P.sepWidth, y2: 0, dash: { length: 1 }, lineWidth: 0.5, lineColor: '#999' }],
    margin: [0, 4, 0, 4],
  });

  const row2 = (label, value, opts = {}) => ({
    columns: [
      { text: label, fontSize: opts.size || 8, bold: !!opts.bold },
      { text: String(value ?? ''), fontSize: opts.size || 8, bold: !!opts.bold, alignment: 'right', width: P.amt },
    ],
    margin: [0, 0, 0, 1],
  });

  const seller = shopTax.tax_entity_name || brandName || '';
  const content = [];

  if (seller) {
    content.push({ text: t(seller), alignment: 'center', fontSize: 12, bold: true });
  }
  if (row.branch) {
    content.push({ text: t('Branch') + ': ' + row.branch, alignment: 'center', fontSize: 8, margin: [0, 1, 0, 0] });
  }
  if (shopTax.tax_id) {
    content.push({ text: t('Tax ID') + ': ' + shopTax.tax_id, alignment: 'center', fontSize: 8, margin: [0, 1, 0, 0] });
  }

  content.push({ text: t('Daily Sales Summary'), alignment: 'center', fontSize: 10, bold: true, margin: [0, 6, 0, 0] });
  content.push({ text: 'Z-REPORT', alignment: 'center', fontSize: 9, bold: true, margin: [0, 0, 0, 2] });
  content.push(line());

  content.push(row2(t('Date'), row.date));
  content.push(row2(t('POS Device'), row.device_name || t('Unassigned device')));
  // เลขประจำเครื่องที่สรรพากรออกให้ (ภ.พ.06) — ตัวที่เจ้าหน้าที่ใช้จับคู่กับทะเบียนที่อนุมัติ
  content.push(row2('POS', row.pos_device_code || ''));
  content.push(line());

  content.push(row2(t('Documents'), String(row.bill_count ?? 0)));
  if (row.invoice_no_list) {
    // ขึ้นบรรทัดใหม่ให้เต็มความกว้าง — เลขใบกำกับยาวเกินกว่าจะวางคู่กับ label บนสลิป
    content.push({ text: t('Invoice Numbers Issued'), fontSize: 8, margin: [0, 2, 0, 0] });
    content.push({ text: row.invoice_no_prefix || '', fontSize: 7, alignment: 'right' });
    content.push({ text: row.invoice_no_list, fontSize: 7, alignment: 'right', margin: [0, 0, 0, 1] });
  }
  content.push(line());

  content.push(row2(t('Taxable'), fmt(row.taxable_amount)));
  content.push(row2(`${t('VAT')} (${toNum(shopTax.vat_rate) || 7}%)`, fmt(row.vat_amount)));
  content.push(row2(t('Total Amount'), fmt(row.total_amount), { bold: true, size: 9 }));

  /**
   * แยกให้เห็นว่าเงินก้อนไหน "ไม่ได้อยู่ในลิ้นชัก" — โชว์เฉพาะตอนมีจริง
   * ไม่งั้นสลิปของร้านที่ไม่ได้ขายเดลิเวอรี่จะรกโดยไม่จำเป็น
   */
  if (toNum(row.platform_amount) > 0) {
    content.push(line());
    content.push(row2(t('In-store sales'), fmt(row.store_amount)));
    content.push(row2(t('Delivery and back office'), fmt(row.platform_amount)));
    content.push({
      text: '* ' + t('Delivery and back-office sales are not cash in the drawer.'),
      fontSize: 7,
      margin: [0, 2, 0, 0],
    });
  }

  /**
   * ยอดขายสะสม — หัวใจของสลิปใบนี้ในสายตาสรรพากร
   * รันต่อเนื่องตั้งแต่บิลใบแรกของเครื่อง ไม่รีเซ็ตเมื่อปิดวัน (เทียบเท่าตัวนับ GT
   * ของเครื่องแคชเชียร์ฮาร์ดแวร์) · เครื่องที่ไม่ได้ลงทะเบียนจะไม่มีค่าให้พิมพ์
   */
  if (row.grand_total_before !== null && row.grand_total_before !== undefined) {
    content.push(line());
    content.push(row2(t('Previous Grand Total'), fmt(row.grand_total_before)));
    content.push(row2(t('Current Grand Total'), fmt(row.grand_total_after), { bold: true, size: 9 }));
  }

  content.push(line());
  content.push({
    text: `${t('Printed at')} ${new Date().toLocaleString('th-TH')}`,
    alignment: 'center',
    fontSize: 6,
    margin: [0, 4, 0, 0],
  });

  const poweredBy = process.env.VUE_APP_POWERED_BY;
  if (poweredBy) {
    content.push({ text: 'Powered by ' + poweredBy, alignment: 'center', fontSize: 6, color: '#666', margin: [0, 2, 0, 0] });
  }

  return {
    pageSize: { width: P.pageWidth, height: 'auto' },
    pageMargins: [P.sideMargin, 6, P.sideMargin, 6],
    defaultStyle: { font: 'Sarabun', fontSize: 8 },
    header: null,
    footer: null,
    content,
  };
}

/**
 * พิมพ์สลิป Z-Report ของ 1 แถวในรายงานรายเครื่อง
 * เดินเส้นทางเดียวกับใบเสร็จ: WebUSB ก่อน ตกไป PDF ถ้าเครื่องพิมพ์ไม่พร้อม
 */
export async function printZReportSlip({ row, shopTax, paperWidth = '72mm', fmt, t = (s) => s, brandName }) {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const popup = (isMobile && !escposIsSupported()) ? window.open('', '_blank') : null;

  try {
    const docDef = buildZReportDocDefinition({ row, shopTax, paperWidth, fmt, t, brandName });

    if (escposIsSupported()) {
      try {
        const canvas = await renderDocDefToCanvas(docDef, { widthPx: dotsForPaperWidth(paperWidth) });
        // รายงานปิดยอด ไม่ใช่การรับเงิน — ห้ามเตะลิ้นชัก
        await printCanvas(canvas, { cashDrawer: false });
        return;
      } catch (e) {
        console.warn('WebUSB Z-Report print failed:', e);
      }
    }
    await printPdf(docDef, { popup });
  } catch (e) {
    if (popup) popup.close();
    throw e;
  }
}
